import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form, Input, Select, Button, Card, Row, Col, DatePicker, Spin, Tag, List, Avatar,
} from "antd";
import {
  UserOutlined, BankOutlined, SearchOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined,
} from "@ant-design/icons";
import { useCreateUser } from "../../../hooks/useCreateUser";
import { useUpdateUser } from "../../../hooks/useUpdateUser";
import { fetchRutificador } from "../../../api/parseDocsService";

const { Option } = Select;

const rutSinDv = (rut = "") => {
  const clean = rut.replace(/[.\s]/g, "").trim();
  if (clean.includes("-")) return clean.split("-")[0];
  return clean.length > 1 ? clean.slice(0, -1) : clean;
};

const isTempEmail = (email = "") =>
  email.includes("@temp.digital-notary") || email.includes("@empresa.digital-notary");

const fullName = (p) =>
  [p?.name, p?.firstLastName, p?.secondLastName].filter(Boolean).join(" ");

const SectionLabel = ({ text }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: "#1d3557", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
    {text}
  </div>
);

const Label = ({ text }) => (
  <span style={{ fontSize: 11, fontWeight: 600, color: "#1d3557", textTransform: "uppercase", letterSpacing: 0.5 }}>
    {text}
  </span>
);

const itemStyle = { marginBottom: 12 };
const sectionStyle = { background: "#f8fafc", borderRadius: 8, padding: "14px 18px 6px", marginBottom: 12 };

const UserCreateForm = ({ user, userId }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const isEditing = !!userId;
  const { mutate, isLoading } = isEditing ? updateUser : createUser;
  const [entityType, setEntityType] = useState(user?.entityType || "person");

  // Lista de personas vinculadas a la empresa
  const [linkedPersons, setLinkedPersons] = useState(
    user?.linkedPersons || []
  );

  // Estado del buscador de personas
  const [repSearchRut, setRepSearchRut] = useState("");
  const [repSearching, setRepSearching] = useState(false);
  const [repCandidate, setRepCandidate] = useState(null); // persona encontrada, pendiente de agregar
  const [repNotFound, setRepNotFound] = useState(false);
  const searchTimeout = useRef(null);

  const onFinish = (values) => {
    const payload = { ...values, dob: values.dob ? values.dob.toISOString() : undefined };

    if (values.entityType === "company") {
      payload.rut = values.companyRut;
      const cleanRut = (values.companyRut || "").replace(/[.\s]/g, "").trim();
      if (!payload.email) payload.email = `${cleanRut}@empresa.digital-notary.cl`;
      payload.password =
        Math.random().toString(36).slice(2) +
        Math.random().toString(36).slice(2) +
        Math.random().toString(36).slice(2);

      // Personas vinculadas → array de RUTs
      payload.linkedPersonRuts = linkedPersons.map((p) => p.rut);

      // Primer representante como legalRep para retrocompatibilidad con contratos
      if (linkedPersons.length > 0) {
        const first = linkedPersons[0];
        payload.legalRepName = fullName(first);
        payload.legalRepRut = first.rut || "";
        // name/firstLastName de la empresa = del primer rep (legacy field, no se muestra)
        if (!isEditing) {
          payload.name = first.name || "";
          payload.firstLastName = first.firstLastName || "";
          payload.secondLastName = first.secondLastName || "";
        }
      }
    }

    if (isEditing) {
      mutate({ id: userId, ...payload }, { onSuccess: () => navigate("/admin/clients") });
    } else {
      mutate(payload, { onSuccess: () => navigate("/admin/clients") });
    }
  };

  const handleRutChange = (e) => {
    if (!isEditing) {
      const base = rutSinDv(e.target.value);
      if (base.length >= 6) form.setFieldValue("password", base);
    }
  };

  const handleRepRutSearch = (value) => {
    setRepSearchRut(value);
    setRepCandidate(null);
    setRepNotFound(false);

    clearTimeout(searchTimeout.current);
    const clean = value.replace(/[.\s]/g, "").trim();
    if (clean.length < 7) return;

    searchTimeout.current = setTimeout(async () => {
      setRepSearching(true);
      try {
        const res = await fetchRutificador(value);
        if (res.data && res.data.rut) {
          setRepCandidate(res.data);
        } else {
          setRepNotFound(true);
        }
      } catch {
        setRepNotFound(true);
      }
      setRepSearching(false);
    }, 500);
  };

  const handleAddPerson = () => {
    if (!repCandidate) return;
    // Evitar duplicados
    if (linkedPersons.some((p) => p.rut === repCandidate.rut)) {
      setRepSearchRut("");
      setRepCandidate(null);
      return;
    }
    setLinkedPersons((prev) => [...prev, repCandidate]);
    setRepSearchRut("");
    setRepCandidate(null);
    setRepNotFound(false);
  };

  const handleRemovePerson = (rut) => {
    setLinkedPersons((prev) => prev.filter((p) => p.rut !== rut));
  };

  return (
    <Card title={isEditing ? "Editar Cliente" : "Nuevo Cliente"} style={{ margin: "0 auto" }}>
      <Form
        form={form}
        name="userForm"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={
          user
            ? {
                name: user.name || "",
                firstLastName: user.firstLastName || "",
                secondLastName: user.secondLastName || "",
                rut: user.rut || "",
                email: user.email || "",
                phone: user.phone || "",
                address: user.address || "",
                commune: user.commune || "",
                civilStatus: user.civilStatus || "no informado",
                role: user.role || "client",
                entityType: user.entityType || "person",
                active: user.active !== undefined ? user.active : true,
                companyName: user.companyName || "",
                companyRut: user.companyRut || "",
                companyGiro: user.companyGiro || "",
                legalRepName: user.legalRepName || "",
                legalRepRut: user.legalRepRut || "",
              }
            : { civilStatus: "no informado", role: "client", entityType: "person", active: true }
        }
      >
        {/* ── Tipo de entidad ─────────────────────────────────────── */}
        <div style={{ ...sectionStyle, marginBottom: 16 }}>
          <SectionLabel text="Tipo de cliente" />
          <Form.Item name="entityType" style={{ marginBottom: 0 }}>
            <Select
              size="large"
              disabled={isEditing}
              onChange={(v) => {
                setEntityType(v);
                setLinkedPersons([]);
                setRepSearchRut("");
                setRepCandidate(null);
              }}
              optionLabelProp="label"
            >
              <Option value="person" label={<span><UserOutlined /> Persona natural</span>}>
                <UserOutlined style={{ marginRight: 8 }} />
                <strong>Persona natural</strong>
                <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>Individuo con RUT personal</span>
              </Option>
              <Option value="company" label={<span><BankOutlined /> Empresa</span>}>
                <BankOutlined style={{ marginRight: 8 }} />
                <strong>Empresa / Persona jurídica</strong>
                <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>Sociedad, EIRL, fundación, etc.</span>
              </Option>
            </Select>
          </Form.Item>
        </div>

        {/* ── Datos empresa ───────────────────────────────────────── */}
        {entityType === "company" && (
          <div style={sectionStyle}>
            <SectionLabel text="Datos de la empresa" />
            <Row gutter={16}>
              <Col xs={24} sm={16}>
                <Form.Item
                  label={<Label text="Razón social" />}
                  name="companyName"
                  style={itemStyle}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Empresa S.A." />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={<Label text="RUT empresa" />}
                  name="companyRut"
                  style={itemStyle}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="76.543.210-K" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label={<Label text="Giro" />} name="companyGiro" style={itemStyle}>
                  <Input placeholder="Giro o actividad comercial" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="legalRepName" hidden><Input /></Form.Item>
            <Form.Item name="legalRepRut" hidden><Input /></Form.Item>
          </div>
        )}

        {/* ── Personas vinculadas (empresa) ───────────────────────── */}
        {entityType === "company" && (
          <div style={sectionStyle}>
            <SectionLabel text="Personas naturales vinculadas" />

            {/* Lista de personas ya agregadas */}
            {linkedPersons.length > 0 && (
              <List
                size="small"
                style={{ marginBottom: 12, background: "#fff", borderRadius: 6, border: "1px solid #e8e8e8" }}
                dataSource={linkedPersons}
                renderItem={(person, idx) => (
                  <List.Item
                    actions={[
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemovePerson(person.rut)}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar size={32} style={{ background: "#1d3557", fontSize: 12 }}>
                          {idx === 0 ? "Rep" : idx + 1}
                        </Avatar>
                      }
                      title={
                        <span style={{ fontWeight: 600 }}>
                          {fullName(person)}
                          {idx === 0 && (
                            <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>
                              Representante legal
                            </Tag>
                          )}
                        </span>
                      }
                      description={
                        <span style={{ fontSize: 12, color: "#666" }}>
                          {person.rut}
                          {person.email && !isTempEmail(person.email) && ` · ${person.email}`}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {linkedPersons.length === 0 && (
              <div style={{ color: "#faad14", fontSize: 12, marginBottom: 12 }}>
                La empresa debe tener al menos una persona vinculada.
              </div>
            )}

            {/* Buscador para agregar persona */}
            <div style={{ padding: "12px 14px", background: "#eef4fb", borderRadius: 6, border: "1px solid #b8d4f0" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1d3557", marginBottom: 8 }}>
                <SearchOutlined style={{ marginRight: 6 }} />
                Buscar persona por RUT y agregar
              </div>
              <Row gutter={12} align="middle">
                <Col xs={24} sm={14}>
                  <Input
                    placeholder="Ej: 17.739.921-3"
                    value={repSearchRut}
                    onChange={(e) => handleRepRutSearch(e.target.value)}
                    suffix={repSearching ? <Spin size="small" /> : <SearchOutlined style={{ color: "#aaa" }} />}
                    allowClear
                    onClear={() => { setRepSearchRut(""); setRepCandidate(null); setRepNotFound(false); }}
                  />
                </Col>
                <Col xs={24} sm={10}>
                  {repCandidate && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddPerson}
                      style={{ background: "#1d3557", borderColor: "#1d3557" }}
                    >
                      Agregar
                    </Button>
                  )}
                  {repNotFound && (
                    <span style={{ fontSize: 12, color: "#999" }}>
                      RUT no registrado
                    </span>
                  )}
                </Col>
              </Row>

              {/* Tarjeta del candidato encontrado */}
              {repCandidate && (
                <div style={{ marginTop: 10, padding: "8px 12px", background: "#f6ffed", borderRadius: 6, border: "1px solid #b7eb8f" }}>
                  <Row gutter={16} align="middle">
                    <Col xs={24} sm={1}>
                      <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                    </Col>
                    <Col xs={24} sm={8}>
                      <div style={{ fontSize: 11, color: "#666" }}>Nombre</div>
                      <div style={{ fontWeight: 600 }}>{fullName(repCandidate)}</div>
                    </Col>
                    <Col xs={24} sm={5}>
                      <div style={{ fontSize: 11, color: "#666" }}>RUT</div>
                      <div style={{ fontWeight: 600 }}>{repCandidate.rut}</div>
                    </Col>
                    <Col xs={24} sm={9}>
                      <div style={{ fontSize: 11, color: "#666" }}>Email</div>
                      <div style={{ fontWeight: 600 }}>
                        {repCandidate.email && !isTempEmail(repCandidate.email)
                          ? repCandidate.email
                          : <span style={{ color: "#aaa" }}>—</span>}
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Información personal (persona natural) ──────────────── */}
        {entityType === "person" && (
          <div style={sectionStyle}>
            <SectionLabel text="Información Personal" />
            <PersonContactFields
              entityType="person"
              itemStyle={itemStyle}
              Label={Label}
              showRut
              handleRutChange={handleRutChange}
            />
          </div>
        )}

        {/* ── Empresa vinculada (solo lectura, persona natural) ──────── */}
        {entityType === "person" && isEditing && user?.linkedCompanyId && (
          <div style={{ ...sectionStyle, border: "1px solid #d6e4f7", background: "#f0f6ff" }}>
            <SectionLabel text="Empresa vinculada" />
            <Row gutter={16}>
              <Col xs={24} sm={14}>
                <div style={itemStyle}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>RAZÓN SOCIAL</div>
                  <div style={{ fontWeight: 600 }}>{user.linkedCompanyId.companyName || "—"}</div>
                </div>
              </Col>
              <Col xs={24} sm={10}>
                <div style={itemStyle}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>RUT EMPRESA</div>
                  <div style={{ fontWeight: 600 }}>{user.linkedCompanyId.companyRut || "—"}</div>
                </div>
              </Col>
              {user.linkedCompanyId.companyGiro && (
                <Col xs={24} sm={14}>
                  <div style={itemStyle}>
                    <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>GIRO</div>
                    <div>{user.linkedCompanyId.companyGiro}</div>
                  </div>
                </Col>
              )}
            </Row>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
              Para editar los datos de la empresa, ve a su perfil directamente.
            </div>
          </div>
        )}

        {/* ── Contacto ────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <SectionLabel text={entityType === "company" ? "Contacto de la empresa" : "Contacto y Ubicación"} />
          <Row gutter={16}>
            <Col xs={24} sm={entityType === "person" ? 14 : 12}>
              <Form.Item
                label={<Label text="Email" />}
                name="email"
                style={itemStyle}
                rules={[
                  { required: entityType === "person", message: "Requerido" },
                  { type: "email", message: "Email inválido" },
                ]}
              >
                <Input placeholder="email@ejemplo.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={entityType === "person" ? 10 : 12}>
              <Form.Item label={<Label text="Teléfono" />} name="phone" style={itemStyle}>
                <Input placeholder="+56 9 1234 5678" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item label={<Label text="Dirección" />} name="address" style={itemStyle}>
                <Input placeholder="Calle, número, departamento" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={<Label text="Comuna" />} name="commune" style={itemStyle}>
                <Input placeholder="Comuna" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* ── Configuración (persona natural) ─────────────────────── */}
        {entityType === "person" && (
          <div style={sectionStyle}>
            <SectionLabel text="Configuración" />
            <Row gutter={16}>
              <Col xs={8}>
                <Form.Item
                  label={<Label text="Rol" />}
                  name="role"
                  style={itemStyle}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Select>
                    <Option value="client">Cliente</Option>
                    <Option value="user">Usuario</Option>
                    <Option value="admin">Administrador</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={8}>
                <Form.Item label={<Label text="Estado" />} name="active" style={itemStyle}>
                  <Select>
                    <Option value={true}>Activo</Option>
                    <Option value={false}>Inactivo</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={8}>
                <Form.Item
                  label={<Label text="Contraseña" />}
                  name="password"
                  style={itemStyle}
                  extra={
                    !isEditing && (
                      <span style={{ fontSize: 11, color: "#888" }}>
                        Se llena con el RUT sin dígito verificador.
                      </span>
                    )
                  }
                  rules={[
                    { required: !isEditing, message: "Requerida" },
                    { min: 6, message: "Mínimo 6 caracteres" },
                  ]}
                >
                  <Input.Password
                    placeholder={isEditing ? "Dejar vacío para no cambiar" : "RUT sin dígito verificador"}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        {/* Campos ocultos para empresa */}
        {entityType === "company" && (
          <>
            <Form.Item name="role" hidden initialValue="client"><Input /></Form.Item>
            <Form.Item name="active" hidden initialValue={true}><Input /></Form.Item>
            <Form.Item name="rut" hidden><Input /></Form.Item>
          </>
        )}

        {/* ── Botones ──────────────────────────────────────────────── */}
        <Row justify="end" style={{ marginTop: 8 }}>
          <Col>
            <Button onClick={() => navigate("/admin/clients")} style={{ marginRight: 8 }}>
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              style={{ background: "#1d3557", borderColor: "#1d3557" }}
            >
              {isEditing ? "Actualizar" : "Crear"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

const PersonContactFields = ({ entityType, itemStyle, Label, showRut = false, handleRutChange }) => (
  <Row gutter={16}>
    <Col xs={24} sm={8}>
      <Form.Item
        label={<Label text="Nombre(s)" />}
        name="name"
        style={itemStyle}
        rules={[{ required: true, message: "Requerido" }]}
      >
        <Input placeholder="Nombre(s)" />
      </Form.Item>
    </Col>
    <Col xs={24} sm={8}>
      <Form.Item
        label={<Label text="Apellido Paterno" />}
        name="firstLastName"
        style={itemStyle}
        rules={[{ required: true, message: "Requerido" }]}
      >
        <Input placeholder="Apellido paterno" />
      </Form.Item>
    </Col>
    <Col xs={24} sm={8}>
      <Form.Item label={<Label text="Apellido Materno" />} name="secondLastName" style={itemStyle}>
        <Input placeholder="Apellido materno" />
      </Form.Item>
    </Col>
    {showRut && (
      <Col xs={24} sm={8}>
        <Form.Item
          label={<Label text="RUT" />}
          name="rut"
          style={itemStyle}
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input placeholder="12345678-9" onChange={handleRutChange} />
        </Form.Item>
      </Col>
    )}
    <Col xs={24} sm={showRut ? 8 : 12}>
      <Form.Item label={<Label text="Fecha de Nacimiento" />} name="dob" style={itemStyle}>
        <DatePicker style={{ width: "100%" }} placeholder="DD/MM/YYYY" format="DD/MM/YYYY" />
      </Form.Item>
    </Col>
    <Col xs={24} sm={showRut ? 8 : 12}>
      <Form.Item label={<Label text="Estado Civil" />} name="civilStatus" style={itemStyle}>
        <Select>
          <Option value="soltero">Soltero/a</Option>
          <Option value="casado">Casado/a</Option>
          <Option value="divorciado">Divorciado/a</Option>
          <Option value="viudo">Viudo/a</Option>
          <Option value="conviviente civil">Conviviente Civil</Option>
          <Option value="separado">Separado/a</Option>
          <Option value="no informado">No Informado</Option>
        </Select>
      </Form.Item>
    </Col>
  </Row>
);

export default UserCreateForm;
